import React from "react";
import PropTypes from "prop-types";
import { Card, Row, Col, Typography, Tag, Space } from "antd";
import { 
  BookOutlined, 
  ClockCircleOutlined, 
  ArrowRightOutlined 
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

const { Text } = Typography;

const RecommendedCoursesCard = ({ recommendedCourses = [], style = {} }) => {
  const navigate = useNavigate();

  const handleCardClick = (courseId) => {
    // Navigate to course detail page using your route path
    navigate(`/available-courses/courses/${courseId}`);
  };

  // Don't render if there are no recommended courses
  if (!recommendedCourses || recommendedCourses.length === 0) {
    return null;
  }

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span role="img" aria-label="sparkles">✨</span>
          <span style={{ fontWeight: 600, fontSize: '16px' }}>Recommended Courses</span>
        </div>
      }
      style={{
        borderRadius: "12px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
        marginBottom: "24px",
        border: "1px solid #f0f0f0",
        ...style
      }}
      headStyle={{
        fontSize: '16px',
        fontWeight: 600,
        padding: '16px 20px',
        borderBottom: "1px solid #f0f0f0",
        backgroundColor: '#fff',
        borderRadius: "12px 12px 0 0"
      }}
      bodyStyle={{ padding: "20px" }}
    >
      <Row gutter={[16, 16]}>
        {recommendedCourses.map((course) => (
          <Col xs={24} sm={12} md={8} lg={6} key={course.id}>
            <Card
              hoverable
              onClick={() => handleCardClick(course.id)}
              style={{
                borderRadius: "10px",
                border: "1px solid #e8e8e8",
                cursor: "pointer",
                padding: "0",
                overflow: "hidden",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.06)",
                height: "100%",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 8px 24px rgba(24, 144, 255, 0.15)",
                  borderColor: "#1890ff"
                }
              }}
              bodyStyle={{ 
                padding: "16px",
                height: "100%",
                display: "flex",
                flexDirection: "column"
              }}
            >
              <div style={{ 
                display: "flex", 
                alignItems: "flex-start", 
                marginBottom: "12px" 
              }}>
                <div style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  backgroundColor: "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
                  background: "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "12px",
                  flexShrink: 0,
                  boxShadow: "0 2px 8px rgba(24, 144, 255, 0.3)"
                }}>
                  <BookOutlined style={{ fontSize: "18px", color: "#fff" }} />
                </div>
                
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text 
                    strong 
                    style={{ 
                      fontSize: "14px",
                      display: "block",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      marginBottom: "6px",
                      color: "#1d1d1d",
                      fontWeight: 600
                    }}
                  >
                    {course.title}
                  </Text>
                  
                  {course.estimatedTime && (
                    <Tag 
                      color="blue" 
                      icon={<ClockCircleOutlined />}
                      style={{ 
                        fontSize: "10px",
                        padding: "3px 8px",
                        margin: 0,
                        lineHeight: "14px",
                        backgroundColor: "rgba(24, 144, 255, 0.08)",
                        borderColor: "rgba(24, 144, 255, 0.2)",
                        color: "#1890ff",
                        borderRadius: "4px",
                        boxShadow: "0 1px 2px rgba(24, 144, 255, 0.1)"
                      }}
                    >
                      {course.estimatedTime.replace('00:', '')}
                    </Tag>
                  )}
                </div>
              </div>

              <div style={{ 
                fontSize: "12px", 
                color: "#666", 
                marginBottom: "12px",
                height: "36px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                lineHeight: "1.5",
                flex: 1
              }}>
                {course.description || "No description available"}
              </div>

              <div style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center",
                marginTop: "auto",
                paddingTop: "12px",
                borderTop: "1px solid #f5f5f5"
              }}>
                <Text type="secondary" style={{ fontSize: "11px", color: "#8c8c8c" }}>
                  {course.moduleCount || 0} module{(course.moduleCount || 0) !== 1 ? 's' : ''}
                </Text>
                
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCardClick(course.id);
                  }}
                  style={{ 
                    fontSize: "11px", 
                    color: "#1890ff",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    fontWeight: 500,
                    cursor: "pointer",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor: "rgba(24, 144, 255, 0.06)",
                      transform: "translateX(2px)"
                    }
                  }}
                >
                  View Details
                  <ArrowRightOutlined style={{ fontSize: "10px" }} />
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </Card>
  );
};

// PropTypes validation
RecommendedCoursesCard.propTypes = {
  recommendedCourses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      estimatedTime: PropTypes.string,
      moduleCount: PropTypes.number
    })
  ),
  style: PropTypes.object
};

// Default props
RecommendedCoursesCard.defaultProps = {
  recommendedCourses: [],
  style: {}
};

export default RecommendedCoursesCard;